import { forwardRef, type ReactNode, useEffect, useRef } from 'react';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { ModuleHeroContent } from './ModuleHeroContent';

const Root = styled.header`
  position: relative;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100vw;
  height: calc(100vh - 95px);
  padding: 32px 24px;
  box-shadow: 0 4px 8px rgb(0 0 0 / 30%);

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 64px 72px;
    background-position: right;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 8vh 135px;

    @media (min-height: 850px) {
      padding: 14vh 135px;
    }
  }
`;

const Video = styled.video`
  position: absolute;
  right: 0;
  bottom: 0;
  min-width: 100%;
  min-height: 100%;
  z-index: -100;
`;

type ModuleHeroVideoProps = {
  bgLoaded: () => void;
  showArrow?: boolean;
  textColor: string;
  url: string;
  testId: string;
  content: {
    title: string | ReactNode;
    paragraphMobile: ReactNode;
    paragraphDesktop: ReactNode;
  };
};

export const ModuleHeroVideo = forwardRef<HTMLDivElement, ModuleHeroVideoProps>(
  ({ url, textColor, bgLoaded, showArrow = false, testId, content }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
      if (videoRef?.current) {
        videoRef.current.onloadeddata = () => {
          bgLoaded();
        };
      }
    }, []);

    return (
      <Root ref={ref} data-testid={testId}>
        <Video
          ref={videoRef}
          autoPlay
          controls
          data-testid={`video-${testId}`}
          loop
          muted
          playsInline
        >
          <source src={url} type="video/mp4" />
        </Video>
        <ModuleHeroContent content={content} showArrow={showArrow} textColor={textColor} />
      </Root>
    );
  },
);

ModuleHeroVideo.displayName = 'ModuleHeroVideo';
